import type { DisplayCue, Language, ProductInfo } from "@expert/contentful";

import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import type { EligibilityMeta, EligibilityStatus, EligibleProduct, SmartLinkMeta } from "../types";

import { getIsProd } from "../config";
import { SaleProgress } from "../types";
import { defaultEligibilityMeta, normalizeEligibilityMeta } from "./useProductOfferStore.helpers";

type ProductState = {
    products: EligibleProduct[];
    selectedProduct: EligibleProduct | undefined;
    displayCues?: DisplayCue[];
};

type SmartLinkState = Record<string, SmartLinkMeta>;

interface ProductOfferStore {
    productStateBySession: Record<string, ProductState>;
    eligibilityMetaBySession: Record<string, EligibilityMeta>;
    smartLinkMetaBySession: Record<string, SmartLinkState>;

    getProductState: (sessionId: string) => ProductState;
    getProducts: (sessionId: string) => EligibleProduct[];
    getPrimaryProducts: (sessionId: string) => EligibleProduct[];
    getOtherProducts: (sessionId: string) => EligibleProduct[];
    getSelectedProduct: (sessionId: string) => EligibleProduct | undefined;
    getSessionIdForProductId: (productId: string) => string | undefined;
    getEligibilityMetaBySessionId: (sessionId: string) => EligibilityMeta;
    getSmartLinkMetaBySessionId: (sessionId: string, productOfferId: string) => SmartLinkMeta;
    getDisplayCues: (sessionId: string) => DisplayCue[] | undefined;
    updateProducts: (sessionId: string, product: EligibleProduct[], displayCues?: DisplayCue[]) => void;
    updateProduct: (
        sessionId: string,
        productId: string,
        change: { saleProgress?: SaleProgress; status?: EligibilityStatus; isConfettiShown?: boolean },
    ) => void;
    updateProductsConfettiStatus: (sessionId: string) => void;
    updateSelectedProduct: (sessionId: string, product: EligibleProduct | undefined) => void;
    updateEligibilityMetaBySessionId: (
        sessionId: string,
        eligibilityMeta: EligibilityMeta | Partial<EligibilityMeta>,
    ) => void;
    updateSmartLinkMetaBySessionId: (sessionId: string, productOfferId: string, smartLinkMeta: SmartLinkMeta) => void;

    isPrimaryProductInProgress: (sessionId: string) => boolean;
    getProductInfo: (sessionId: string, sku?: string, lang?: Language) => ProductInfo | undefined;
    resetSaleSession: (sessionId: string) => void;
}

export const useProductOfferStore = createWithEqualityFn<ProductOfferStore>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    productStateBySession: {},
                    eligibilityMetaBySession: {},
                    smartLinkMetaBySession: {},
                    updateProducts: (sessionId, products, displayCues) => {
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                const existingProducts = currentProductState.products;

                                // try to maintain sales progress state of the current products when setting new products
                                const updatedProducts = products.map((newProduct) => {
                                    // find matching product from new state in the existing state
                                    const matchingExistingProduct = existingProducts.find(
                                        (existingProduct) => existingProduct.id === newProduct.id,
                                    );

                                    // if no match, do not alter the incoming product
                                    if (!matchingExistingProduct) {
                                        return newProduct;
                                    }

                                    // if match, maintain the existing state sale progress
                                    return {
                                        ...newProduct,
                                        saleProgress: matchingExistingProduct.saleProgress,
                                    };
                                });

                                (state.productStateBySession[sessionId] as ProductState) = {
                                    ...currentProductState,
                                    products: updatedProducts,
                                    displayCues,
                                };
                            },
                            false,
                            "updateProducts",
                        );
                    },
                    updateProduct: (sessionId, productId, change) => {
                        set(
                            (state) => {
                                const { products, selectedProduct } = state.getProductState(sessionId);
                                if (!products) {
                                    return;
                                }

                                const newPrimaryProducts = products.map((currentProduct) =>
                                    currentProduct.id === productId ? { ...currentProduct, ...change } : currentProduct,
                                );

                                const newSelectedProduct =
                                    selectedProduct?.id === productId
                                        ? { ...selectedProduct, ...change }
                                        : selectedProduct;

                                (state.productStateBySession[sessionId] as ProductState) = {
                                    ...state.productStateBySession[sessionId],
                                    products: newPrimaryProducts,
                                    selectedProduct: newSelectedProduct,
                                };
                            },
                            false,
                            "updateProduct",
                        );
                    },
                    updateProductsConfettiStatus: (sessionId) =>
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                const updatedProducts = currentProductState.products.map((product) => {
                                    if (product.saleProgress === SaleProgress.Success && !product.isConfettiShown) {
                                        return { ...product, isConfettiShown: true };
                                    }
                                    return product;
                                });
                                (state.productStateBySession[sessionId] as ProductState) = {
                                    ...currentProductState,
                                    products: updatedProducts,
                                };
                            },
                            false,
                            "updateProductsConfettiStatus",
                        ),
                    updateSelectedProduct: (sessionId, product) =>
                        set(
                            (state) => {
                                const currentProductState = state.getProductState(sessionId);
                                (state.productStateBySession[sessionId] as ProductState) = {
                                    ...currentProductState,
                                    selectedProduct: product,
                                };
                            },
                            false,
                            "updateSelectedProduct",
                        ),
                    updateEligibilityMetaBySessionId: (sessionId, eligibilityMeta) => {
                        set(
                            (state) => {
                                state.eligibilityMetaBySession[sessionId] = normalizeEligibilityMeta(eligibilityMeta);
                            },
                            false,
                            "updateEligibilityMeta",
                        );
                    },
                    updateSmartLinkMetaBySessionId: (sessionId, productOfferId, smartLinkMeta) => {
                        set(
                            (state) => {
                                if (!state.smartLinkMetaBySession[sessionId]) {
                                    state.smartLinkMetaBySession[sessionId] = {};
                                }
                                state.smartLinkMetaBySession[sessionId][productOfferId] = smartLinkMeta;
                            },
                            false,
                            "updateSmartLinkMeta",
                        );
                    },
                    isPrimaryProductInProgress: (sessionId) => {
                        return (
                            get()
                                .getPrimaryProducts(sessionId)
                                ?.some((p) => p.saleProgress === SaleProgress.InProgress) ?? false
                        );
                    },
                    getProductState: (sessionId) => {
                        return (
                            get().productStateBySession[sessionId] ?? {
                                products: [],
                                selectedProduct: undefined,
                            }
                        );
                    },
                    getProducts: (sessionId) => {
                        return get().getProductState(sessionId).products;
                    },
                    getPrimaryProducts: (sessionId) =>
                        get()
                            .getProductState(sessionId)
                            .products.filter((p) => p.isPrimaryProduct) ?? [],
                    getOtherProducts: (sessionId) =>
                        get()
                            .getProductState(sessionId)
                            .products.filter((p) => !p.isPrimaryProduct) ?? [],
                    getSelectedProduct: (sessionId) => {
                        return get().getProductState(sessionId).selectedProduct;
                    },
                    getSessionIdForProductId: (productId) => {
                        return Object.keys(get().productStateBySession).find((sessionId) =>
                            get()
                                .getProductState(sessionId)
                                .products.some((product) => product.id === productId),
                        );
                    },
                    getEligibilityMetaBySessionId: (sessionId) => {
                        return get().eligibilityMetaBySession[sessionId] ?? { ...defaultEligibilityMeta };
                    },
                    getProductInfo: (sessionId, sku, lang = "en-US") => {
                        return get()
                            .productStateBySession[sessionId]?.products.filter((p) => p.productSku === sku)
                            .at(0)?.productInfo?.[lang];
                    },
                    getSmartLinkMetaBySessionId: (sessionId, productOfferId) => {
                        const sessionMeta = get().smartLinkMetaBySession[sessionId];
                        if (!sessionMeta) {
                            return { maximumSmartLinksGenerated: false };
                        }
                        return sessionMeta[productOfferId] ?? { maximumSmartLinksGenerated: false };
                    },
                    getDisplayCues: (sessionId) => {
                        return get().productStateBySession?.[sessionId]?.displayCues;
                    },
                    resetSaleSession: (sessionId) =>
                        set(
                            (state) => {
                                delete state.productStateBySession[sessionId];
                            },
                            false,
                            "resetSaleSession",
                        ),
                }),
                {
                    enabled: !getIsProd(),
                    store: "product-offer-store",
                    name: "workspace/sales-tools",
                },
            ),
        ),
        {
            name: "product-offer-store",
            // Skipped, but manually called elsewhere: void useProductOfferStore.persist.rehydrate();
            skipHydration: true,
        },
    ),
    shallow,
);
